import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Checkbox } from '@/components/ui/checkbox';
import { useAdminSequences } from '@/hooks/useAdminSequences';
import { spotifyService, SpotifyTrack, SpotifyArtist } from '@/services/spotifyService';
import { Database } from '@/integrations/supabase/types';
import { RefreshCw, AlertTriangle, CheckCircle, X, Search, Music, Info } from 'lucide-react';
import { toast } from 'sonner';

type Sequence = Database['public']['Tables']['sequences']['Row'];

interface AutoUpdateModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface UpdatePreview {
  sequence: Sequence;
  spotifyTrack: SpotifyTrack | null;
  spotifyArtist?: SpotifyArtist | null;
  error?: string;
  updates: {
    artist_name?: string;
    genre_name?: string; // legacy; kept for compatibility with any old payloads
    spotify_track_id?: string;
    spotify_popularity?: number;
    spotify_duration_ms?: number;
    album?: string;
    image_url?: string;
    duration?: number;
  };
}

interface UpdateStatus {
  sequenceId: string;
  sequenceName: string;
  status: 'pending' | 'processing' | 'completed' | 'error';
  error?: string;
}

export function AutoUpdateModal({ isOpen, onClose }: AutoUpdateModalProps) {
  const {
    findSequencesMissingMetadata,
    updateSequence,
    createArtist,
    createGenre,
    artists,
    genres
  } = useAdminSequences();

  const [sequences, setSequences] = useState<Sequence[]>([]);
  const [previews, setPreviews] = useState<UpdatePreview[]>([]);
  const [loading, setLoading] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [progress, setProgress] = useState(0);
  const [selectedSequences, setSelectedSequences] = useState<Set<string>>(new Set());
  const [phase, setPhase] = useState<'loading' | 'preview' | 'updating' | 'complete'>('loading');
  const [updateStatuses, setUpdateStatuses] = useState<UpdateStatus[]>([]);
  const [currentlyProcessing, setCurrentlyProcessing] = useState<string>('');

  // Load sequences missing metadata
  useEffect(() => {
    if (isOpen) {
      loadSequencesMissingMetadata();
    }
  }, [isOpen]);

  const loadSequencesMissingMetadata = async () => {
    setLoading(true);
    setPhase('loading');
    
    try {
      const missingSequences = await findSequencesMissingMetadata();
      setSequences(missingSequences);
      
      if (missingSequences.length === 0) {
        toast.success('All sequences have complete metadata!');
        onClose();
        return;
      }

      // Generate previews by searching Spotify
      await generatePreviews(missingSequences);
    } catch (error) {
      console.error('Error loading sequences:', error);
      toast.error('Failed to load sequences');
    } finally {
      setLoading(false);
    }
  };

  const generatePreviews = async (sequencesToUpdate: Sequence[]) => {
    setPhase('preview');
    const previewResults: UpdatePreview[] = [];
    
    for (let i = 0; i < sequencesToUpdate.length; i++) {
      const sequence = sequencesToUpdate[i];
      setProgress((i / sequencesToUpdate.length) * 100);
      
      try {
        // Use sequence title for search, fallback to name
        const searchTerm = sequence.title || sequence.name || '';
        
        if (!searchTerm) {
          previewResults.push({
            sequence,
            spotifyTrack: null,
            error: 'No title or name to search with',
            updates: {}
          });
          continue;
        }

        // Search Spotify
        const searchResults = await spotifyService.searchTracks(searchTerm, 1);
        const spotifyTrack = searchResults[0] || null;
        
        if (!spotifyTrack) {
          previewResults.push({
            sequence,
            spotifyTrack: null,
            error: 'No Spotify match found',
            updates: {}
          });
          continue;
        }

        // Prepare updates
        const updates: UpdatePreview['updates'] = {};
        
        if (!sequence.spotify_track_id) {
          updates.spotify_track_id = spotifyTrack.id;
        }
        if (!sequence.spotify_popularity) {
          updates.spotify_popularity = spotifyTrack.popularity;
        }
        if (!sequence.spotify_duration_ms) {
          updates.spotify_duration_ms = spotifyTrack.duration_ms;
        }
        if (!sequence.album) {
          updates.album = spotifyTrack.album.name;
        }
        if (!sequence.image_url && spotifyTrack.album.images[0]) {
          updates.image_url = spotifyTrack.album.images[0].url;
        }
        if (!sequence.duration) {
          updates.duration = Math.round(spotifyTrack.duration_ms / 1000);
        }

        // Check for artist
        if (!sequence.artist_id && spotifyTrack.artists[0]) {
          const artistName = spotifyTrack.artists[0].name;
          const existingArtist = artists.find(a => 
            a.name.toLowerCase() === artistName.toLowerCase()
          );
          
          if (!existingArtist) {
            updates.artist_name = artistName;
          }
        }

        // Fetch artist details for genre information
        let spotifyArtist: SpotifyArtist | null = null;
        if (spotifyTrack.artists[0]) {
          try {
            spotifyArtist = await spotifyService.getArtist(spotifyTrack.artists[0].id);
            
            // Check for genre if we have artist genres
            if (!sequence.genre_id && spotifyArtist && spotifyArtist.genres.length > 0) {
              const mappedGenre = spotifyService.mapSpotifyGenreToDbGenre(spotifyArtist.genres);
              if (mappedGenre) {
                const existingGenre = genres.find(g => 
                  g.name.toLowerCase() === mappedGenre.toLowerCase()
                );
                
                if (!existingGenre) {
                  updates.genre_name = mappedGenre;
                }
              }
            }
          } catch (error) {
            console.error('Error fetching artist details:', error);
          }
        }

        previewResults.push({
          sequence,
          spotifyTrack,
          spotifyArtist,
          updates
        });

        // Select by default if there are updates
        if (Object.keys(updates).length > 0) {
          setSelectedSequences(prev => new Set([...prev, sequence.id]));
        }

      } catch (error) {
        console.error(`Error processing sequence ${sequence.id}:`, error);
        previewResults.push({
          sequence,
          spotifyTrack: null,
          error: 'Search failed',
          updates: {}
        });
      }

      // Small delay to avoid rate limiting
      await new Promise(resolve => setTimeout(resolve, 100));
    }
    
    setPreviews(previewResults);
    setProgress(100);
  };

  const handleSequenceToggle = (sequenceId: string) => {
    setSelectedSequences(prev => {
      const newSet = new Set(prev);
      if (newSet.has(sequenceId)) {
        newSet.delete(sequenceId);
      } else {
        newSet.add(sequenceId);
      }
      return newSet;
    });
  };

  const handleSelectAll = () => {
    const updatableSequences = previews
      .filter(p => Object.keys(p.updates).length > 0)
      .map(p => p.sequence.id);
    
    setSelectedSequences(new Set(updatableSequences));
  };

  const handleDeselectAll = () => {
    setSelectedSequences(new Set());
  };

  const executeUpdates = async () => {
    setUpdating(true);
    setPhase('updating');
    setProgress(0);
    
    const selectedPreviews = previews.filter(p => selectedSequences.has(p.sequence.id));
    
    // Initialize update statuses
    const statuses: UpdateStatus[] = selectedPreviews.map(p => ({
      sequenceId: p.sequence.id,
      sequenceName: p.sequence.title || p.sequence.name || 'Unknown',
      status: 'pending' as const
    }));
    setUpdateStatuses(statuses);
    
    let successCount = 0;
    let errorCount = 0;

    for (let i = 0; i < selectedPreviews.length; i++) {
      const preview = selectedPreviews[i];
      const sequenceName = preview.sequence.title || preview.sequence.name || 'Unknown';
      
      // Update current processing
      setCurrentlyProcessing(sequenceName);
      setUpdateStatuses(prev => prev.map(s => 
        s.sequenceId === preview.sequence.id 
          ? { ...s, status: 'processing' as const }
          : s
      ));
      
      setProgress(((i + 0.5) / selectedPreviews.length) * 100);
      
      try {
        const updates: Record<string, unknown> = { ...preview.updates };
        
        // Create artist if needed
        if (preview.updates.artist_name) {
          const artist = await createArtist(
            preview.updates.artist_name,
            `Artist for ${preview.updates.artist_name}`,
            preview.spotifyTrack?.album.images[0]?.url
          );
          updates.artist_id = artist.id;
          delete updates.artist_name;
        }

        // Create genre if needed
        if (preview.updates.genre_name) {
          const genre = await createGenre(preview.updates.genre_name);
          updates.genre_id = genre.id;
          delete updates.genre_name;
        }

        // Update sequence
        await updateSequence(preview.sequence.id, updates);
        successCount++;
        
        // Update status to completed
        setUpdateStatuses(prev => prev.map(s => 
          s.sequenceId === preview.sequence.id 
            ? { ...s, status: 'completed' as const }
            : s
        ));
        
      } catch (error) {
        console.error(`Failed to update sequence ${preview.sequence.id}:`, error);
        errorCount++;
        
        // Update status to error
        setUpdateStatuses(prev => prev.map(s => 
          s.sequenceId === preview.sequence.id 
            ? { ...s, status: 'error' as const, error: error instanceof Error ? error.message : 'Unknown error' }
            : s
        ));
      }
      
      setProgress(((i + 1) / selectedPreviews.length) * 100);
    }
    
    setCurrentlyProcessing('');
    setPhase('complete');
    
    toast.success(`Updated ${successCount} sequences successfully${errorCount > 0 ? `, ${errorCount} failed` : ''}`);
    
    setTimeout(() => {
      onClose();
    }, 3000);
  };

  const getUpdateCount = (updates: UpdatePreview['updates']) => {
    return Object.keys(updates).length;
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <RefreshCw className="h-5 w-5" />
            Auto-Update Missing Metadata
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto space-y-4">
          {/* Summary Section - always visible */}
          {(phase === 'preview' || phase === 'updating' || phase === 'complete') && sequences.length > 0 && (
            <Alert className="border-blue-200 bg-blue-50">
              <Info className="h-4 w-4 text-blue-600" />
              <AlertDescription className="text-blue-800">
                <div className="font-medium">Sequences to Update:</div>
                <div className="text-sm mt-1">
                  {sequences.slice(0, 3).map(seq => seq.title || seq.name).join(', ')}
                  {sequences.length > 3 && ` and ${sequences.length - 3} more`}
                </div>
              </AlertDescription>
            </Alert>
          )}

          {phase === 'loading' && (
            <div className="space-y-4">
              <Alert>
                <Search className="h-4 w-4" />
                <AlertDescription>
                  Searching for sequences with missing metadata and finding Spotify matches...
                </AlertDescription>
              </Alert>
              <Progress value={progress} />
              
              {/* Show partial results while loading */}
              {sequences.length > 0 && (
                <div className="text-sm text-muted-foreground">
                  Found {sequences.length} sequences with missing metadata
                </div>
              )}
            </div>
          )}

          {phase === 'preview' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-medium">
                    Found {sequences.length} sequences with missing metadata
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    {selectedSequences.size} selected for update
                  </p>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={handleSelectAll}>
                    Select All
                  </Button>
                  <Button variant="outline" size="sm" onClick={handleDeselectAll}>
                    Deselect All
                  </Button>
                </div>
              </div>

              <div className="max-h-96 overflow-y-auto space-y-2">
                {previews.map((preview) => (
                  <div
                    key={preview.sequence.id}
                    className={`border rounded-lg p-4 ${
                      selectedSequences.has(preview.sequence.id) 
                        ? 'border-primary bg-primary/5' 
                        : 'border-gray-200'
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <Checkbox
                        checked={selectedSequences.has(preview.sequence.id)}
                        onCheckedChange={() => handleSequenceToggle(preview.sequence.id)}
                        disabled={getUpdateCount(preview.updates) === 0}
                      />
                      
                      <div className="flex-1 space-y-2">
                        <div className="flex items-center justify-between">
                          <h4 className="font-medium">
                            {preview.sequence.title || preview.sequence.name}
                          </h4>
                          <div className="flex gap-2">
                            {getUpdateCount(preview.updates) > 0 ? (
                              <Badge variant="secondary">
                                {getUpdateCount(preview.updates)} updates
                              </Badge>
                            ) : (
                              <Badge variant="outline">No updates</Badge>
                            )}
                          </div>
                        </div>

                        {preview.error ? (
                          <Alert className="border-red-200 bg-red-50">
                            <AlertTriangle className="h-4 w-4 text-red-600" />
                            <AlertDescription className="text-red-800">
                              {preview.error}
                            </AlertDescription>
                          </Alert>
                        ) : preview.spotifyTrack ? (
                          <div className="bg-green-50 p-3 rounded border border-green-200">
                            <div className="flex items-center gap-2 mb-2">
                              <Music className="h-4 w-4 text-green-600" />
                              <span className="font-medium text-green-800">
                                Found: {preview.spotifyTrack.name} by {preview.spotifyTrack.artists[0]?.name}
                              </span>
                              {preview.spotifyArtist && preview.spotifyArtist.genres.length > 0 && (
                                <span className="text-xs text-green-600 ml-2">
                                  (Genres: {preview.spotifyArtist.genres.slice(0, 3).join(', ')})
                                </span>
                              )}
                            </div>
                            <div className="text-sm text-green-700 space-y-1">
                              {Object.entries(preview.updates).map(([key, value]) => {
                                const displayKey = key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
                                return (
                                  <p key={key}>
                                    <span className="font-medium">{displayKey}:</span> {value}
                                  </p>
                                );
                              })}
                            </div>
                          </div>
                        ) : null}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {phase === 'updating' && (
            <div className="space-y-4">
              <Alert>
                <RefreshCw className="h-4 w-4 animate-spin" />
                <AlertDescription>
                  Updating {selectedSequences.size} sequences...
                  {currentlyProcessing && (
                    <div className="mt-2 text-sm">
                      Currently processing: <span className="font-medium">{currentlyProcessing}</span>
                    </div>
                  )}
                </AlertDescription>
              </Alert>
              <Progress value={progress} />
              
              {/* Real-time status list */}
              <div className="max-h-64 overflow-y-auto space-y-2">
                <h4 className="text-sm font-medium mb-2">Update Status:</h4>
                {updateStatuses.map((status) => (
                  <div
                    key={status.sequenceId}
                    className={`flex items-center justify-between p-2 rounded text-sm ${
                      status.status === 'completed' 
                        ? 'bg-green-50 border border-green-200' 
                        : status.status === 'processing'
                        ? 'bg-blue-50 border border-blue-200'
                        : status.status === 'error'
                        ? 'bg-red-50 border border-red-200'
                        : 'bg-gray-50 border border-gray-200'
                    }`}
                  >
                    <span className={`truncate flex-1 ${
                      status.status === 'completed' ? 'text-green-900' :
                      status.status === 'processing' ? 'text-blue-900' :
                      status.status === 'error' ? 'text-red-900' :
                      'text-gray-900'
                    }`}>{status.sequenceName}</span>
                    <div className="flex items-center gap-2">
                      {status.status === 'completed' && (
                        <CheckCircle className="h-4 w-4 text-green-600" />
                      )}
                      {status.status === 'processing' && (
                        <RefreshCw className="h-4 w-4 text-blue-600 animate-spin" />
                      )}
                      {status.status === 'error' && (
                        <AlertTriangle className="h-4 w-4 text-red-600" />
                      )}
                      {status.status === 'pending' && (
                        <div className="h-4 w-4 rounded-full bg-gray-400" />
                      )}
                      <span className={`text-xs font-medium ${
                        status.status === 'completed' ? 'text-green-700' :
                        status.status === 'processing' ? 'text-blue-700' :
                        status.status === 'error' ? 'text-red-700' :
                        'text-gray-600'
                      }`}>
                        {status.status === 'processing' ? 'Processing...' :
                         status.status === 'completed' ? 'Done' :
                         status.status === 'error' ? 'Failed' :
                         'Waiting'}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {phase === 'complete' && (
            <div className="space-y-4">
              <Alert className="border-green-200 bg-green-50">
                <CheckCircle className="h-4 w-4 text-green-600" />
                <AlertDescription className="text-green-800">
                  Auto-update completed successfully!
                </AlertDescription>
              </Alert>
              
              {/* Final status summary */}
              {updateStatuses.length > 0 && (
                <div className="space-y-2">
                  <h4 className="text-sm font-medium">Final Status:</h4>
                  <div className="max-h-48 overflow-y-auto space-y-1">
                    {updateStatuses.map((status) => (
                      <div
                        key={status.sequenceId}
                        className={`flex items-center justify-between p-2 rounded text-sm ${
                          status.status === 'completed' 
                            ? 'bg-green-50 border border-green-200' 
                            : 'bg-red-50 border border-red-200'
                        }`}
                      >
                        <span className={`truncate flex-1 ${
                          status.status === 'completed' ? 'text-green-900' : 'text-red-900'
                        }`}>{status.sequenceName}</span>
                        <div className="flex items-center gap-2">
                          {status.status === 'completed' ? (
                            <CheckCircle className="h-4 w-4 text-green-600" />
                          ) : (
                            <AlertTriangle className="h-4 w-4 text-red-600" />
                          )}
                          <span className={`text-xs font-medium ${
                            status.status === 'completed' ? 'text-green-700' : 'text-red-700'
                          }`}>
                            {status.status === 'completed' ? 'Updated' : 'Failed'}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          {phase === 'preview' && (
            <>
              <Button variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button
                onClick={executeUpdates}
                disabled={selectedSequences.size === 0}
              >
                Update {selectedSequences.size} Sequences
              </Button>
            </>
          )}
          
          {(phase === 'updating' || phase === 'complete') && (
            <Button variant="outline" onClick={onClose} disabled={phase === 'updating'}>
              {phase === 'updating' ? 'Updating...' : 'Close'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}